/**
 * LoggingFlow — the full symptom logging experience.
 *
 * Architecture:
 *   1. Copy-forward panel  — symptoms from last session, each with Same/Worse/Edit/Gone
 *   2. Category picker     — 6 group cards → symptom chips → inline detail drill-down
 *   3. Save / Tell you later
 *
 * Props:
 *   user          — user object (includes conditions, detail_preference)
 *   dayOfCycle    — number | null
 *   recentSessions — array of past sessions (most recent first) for pattern memory
 *   onSaved       — callback after successful save
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import {
  TAXONOMY,
  SYMPTOM_MAP,
  getCycleAwareSuggestions,
  bumpSeverity,
  attributeSummary,
} from '../data/taxonomy';
import { getAutoSurfaceMap, getConditionSuggestions, checkAnomaly } from '../data/conditionProfiles';
import { getCopyForwardSymptoms, formatLastLogTime, computePatternMemory } from '../data/carryForward';

const C = {
  bg: '#0D0D12', surface: '#16161F', card: '#1C1C28', border: '#2A2A3A',
  accent: '#E8A2B8', accentSoft: '#C97A9A', teal: '#7ECECA', gold: '#D4AF7A',
  text: '#F0EEF5', muted: '#8A8A9E', danger: '#FF6B7A', green: '#7ECBA5', purple: '#B39DDB',
};

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Pill button ──────────────────────────────────────────────────────────────
function Pill({ label, selected, color, onClick, memory }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: selected ? `${color}28` : memory ? `${color}12` : C.surface,
        border: `1px solid ${selected ? color : memory ? `${color}55` : C.border}`,
        color: selected ? color : memory ? `${color}cc` : C.muted,
        borderRadius: 20,
        padding: '5px 12px',
        fontSize: 12,
        cursor: 'pointer',
        fontFamily: "'Inter', sans-serif",
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        transition: 'all 0.12s',
        whiteSpace: 'nowrap',
      }}
    >
      {memory && <span style={{ fontSize: 9 }}>●</span>}
      {label}
    </button>
  );
}

// ─── Symptom detail panel ─────────────────────────────────────────────────────
function SymptomDetail({ symptomKey, attributes, onChange, detailMode, autoSurfaceMap, patternMemory, recentSessions }) {
  const def = SYMPTOM_MAP[symptomKey];
  const autoSurface = (autoSurfaceMap[symptomKey] || []);
  const memory = patternMemory[symptomKey];
  const [anomaly, setAnomaly] = useState(null);
  const [showAll, setShowAll] = useState(detailMode === 'full');

  useEffect(() => {
    setShowAll(detailMode === 'full');
  }, [detailMode]);

  if (!def) return null;

  // Determine which attributes to show
  const visibleAttrs = def.attributes.filter(attr => {
    if (attr.default) return true;
    if (detailMode === 'full' || showAll) return true;
    if (autoSurface.includes(attr.key)) return true;
    return false;
  });

  const hiddenCount = def.attributes.length - visibleAttrs.length;

  const handleChange = (attrKey, value) => {
    const next = { ...attributes, [attrKey]: value };
    onChange(next);
    // Check anomaly for clinical attributes
    const nudge = checkAnomaly(symptomKey, next, recentSessions);
    setAnomaly(nudge);
  };

  return (
    <div style={{
      background: C.surface,
      borderRadius: 12,
      padding: '12px 14px',
      border: `1px solid ${def.groupColor}33`,
      marginTop: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: def.groupColor, fontFamily: 'monospace', letterSpacing: '0.06em' }}>
          {def.label.toUpperCase()}
        </div>
        {memory && (
          <div style={{ fontSize: 10, color: C.green, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span>●</span> usually: {attributeSummary(memory.commonAttributes)}
          </div>
        )}
      </div>

      {visibleAttrs.map(attr => (
        <div key={attr.key} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginBottom: 6 }}>
            {attr.label.toUpperCase()}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {attr.options.map(opt => {
              const isMemory = memory?.commonAttributes[attr.key] === opt && !attributes[attr.key];
              return (
                <Pill
                  key={opt}
                  label={opt}
                  selected={attributes[attr.key] === opt}
                  color={def.groupColor}
                  memory={isMemory}
                  onClick={() => handleChange(attr.key, attributes[attr.key] === opt ? undefined : opt)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {!showAll && hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          style={{ background: 'none', border: 'none', color: C.muted, fontSize: 11, cursor: 'pointer', padding: 0, marginTop: 2 }}
        >
          + {hiddenCount} more detail{hiddenCount > 1 ? 's' : ''}
        </button>
      )}

      {anomaly && (
        <div style={{
          marginTop: 10,
          padding: '8px 10px',
          background: `${C.gold}15`,
          border: `1px solid ${C.gold}44`,
          borderRadius: 8,
          fontSize: 11,
          color: C.gold,
          lineHeight: 1.5,
        }}>
          ⚠ {anomaly}
        </div>
      )}
    </div>
  );
}

// ─── Copy-forward panel ───────────────────────────────────────────────────────
function CopyForwardPanel({ carrySymptoms, prevSession, onAction, onTellYouLater, detailMode, autoSurfaceMap, patternMemory, recentSessions }) {
  const [editing, setEditing] = useState(null); // symptomKey being edited
  const [statuses, setStatuses] = useState(() =>
    Object.fromEntries(carrySymptoms.map(s => [s.key, 'pending']))
  );
  const [editedAttrs, setEditedAttrs] = useState(() =>
    Object.fromEntries(carrySymptoms.map(s => [s.key, { ...s.attributes }]))
  );

  const allHandled = carrySymptoms.every(s => statuses[s.key] !== 'pending');

  const setStatus = (key, status) => {
    setStatuses(prev => ({ ...prev, [key]: status }));
    if (status !== 'editing') setEditing(null);
  };

  const handleWorse = (symptom) => {
    const bumped = bumpSeverity(symptom.key, editedAttrs[symptom.key]);
    setEditedAttrs(prev => ({ ...prev, [symptom.key]: bumped }));
    setStatus(symptom.key, 'worse');
  };

  const confirmAll = () => {
    const confirmed = carrySymptoms
      .filter(s => statuses[s.key] !== 'gone')
      .map(s => ({
        key: s.key,
        attributes: statuses[s.key] === 'editing' ? editedAttrs[s.key] : (
          statuses[s.key] === 'worse' ? editedAttrs[s.key] : s.attributes
        ),
        source: 'copy_forward',
      }));
    onAction(confirmed);
  };

  useEffect(() => {
    if (allHandled) {
      const confirmed = carrySymptoms
        .filter(s => statuses[s.key] !== 'gone')
        .map(s => ({
          key: s.key,
          attributes: editedAttrs[s.key],
          source: 'copy_forward',
        }));
      onAction(confirmed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allHandled]);

  const statusColor = { same: C.green, worse: C.danger, gone: C.muted, pending: C.border, editing: C.teal };

  return (
    <div style={{ background: C.card, borderRadius: 18, border: `1px solid ${C.accent}33`, overflow: 'hidden', marginBottom: 16 }}>
      {/* Header */}
      <div style={{ background: `${C.accent}12`, borderBottom: `1px solid ${C.accent}22`, padding: '12px 16px' }}>
        <div style={{ fontSize: 13, color: C.accent, fontWeight: 600, marginBottom: 2 }}>
          Hi — how are you compared to your last log?
        </div>
        <div style={{ fontSize: 11, color: C.accentSoft }}>
          {formatLastLogTime(prevSession?.logged_at)}
          {carrySymptoms.some(s => s.needsConfirmation) && ' · It\'s been a while — check each one still applies'}
        </div>
      </div>

      {/* Symptom rows */}
      <div style={{ padding: '8px 14px' }}>
        {carrySymptoms.map(symptom => {
          const def = SYMPTOM_MAP[symptom.key];
          if (!def) return null;
          const status = statuses[symptom.key];
          const isEditing = editing === symptom.key;
          const summary = attributeSummary(editedAttrs[symptom.key]);

          return (
            <div key={symptom.key} style={{
              borderBottom: `1px solid ${C.border}`,
              paddingTop: 10,
              paddingBottom: 10,
            }}>
              {/* Prompt-type header */}
              {symptom.isPromptType && status === 'pending' && (
                <div style={{ fontSize: 10, color: C.teal, fontFamily: 'monospace', marginBottom: 4 }}>
                  HOW HAS IT CHANGED?
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                {/* Status indicator */}
                <div style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: statusColor[status] || C.border,
                  marginTop: 6, flexShrink: 0,
                }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: status === 'gone' ? C.muted : C.text }}>
                    {def.label}
                  </div>
                  {summary && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{summary}</div>
                  )}
                </div>

                {/* Action buttons */}
                {status === 'pending' && (
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ActionBtn label="Same" color={C.green} onClick={() => setStatus(symptom.key, 'same')} />
                    <ActionBtn label="Worse" color={C.danger} onClick={() => handleWorse(symptom)} />
                    <ActionBtn label="Edit" color={C.teal} onClick={() => { setEditing(symptom.key); setStatus(symptom.key, 'editing'); }} />
                    <ActionBtn label="Gone ✕" color={C.muted} onClick={() => setStatus(symptom.key, 'gone')} />
                  </div>
                )}
                {status !== 'pending' && status !== 'editing' && (
                  <button onClick={() => { setStatus(symptom.key, 'pending'); setEditing(null); }}
                    style={{ background: 'none', border: 'none', color: C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'monospace' }}>
                    UNDO
                  </button>
                )}
              </div>

              {/* Inline editor */}
              {isEditing && (
                <SymptomDetail
                  symptomKey={symptom.key}
                  attributes={editedAttrs[symptom.key]}
                  onChange={attrs => setEditedAttrs(prev => ({ ...prev, [symptom.key]: attrs }))}
                  detailMode={detailMode}
                  autoSurfaceMap={autoSurfaceMap}
                  patternMemory={patternMemory}
                  recentSessions={recentSessions}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 16px', background: `${C.bg}88`, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={confirmAll}
          style={{ background: 'none', border: 'none', color: C.green, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}
        >
          ✓ All the same
        </button>
        <button
          onClick={onTellYouLater}
          style={{ background: 'none', border: 'none', color: C.muted, fontSize: 12, cursor: 'pointer', fontFamily: "'Inter',sans-serif" }}
        >
          Tell you later →
        </button>
      </div>
    </div>
  );
}

function ActionBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: `${color}18`,
      border: `1px solid ${color}55`,
      color: color,
      borderRadius: 20,
      padding: '4px 11px',
      fontSize: 11,
      cursor: 'pointer',
      fontFamily: "'Inter',sans-serif",
      fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </button>
  );
}

// ─── Category picker ──────────────────────────────────────────────────────────
function CategoryPicker({ currentSymptomKeys, onToggleSymptom, expandedSymptom, onSetExpanded, detailMode, autoSurfaceMap, patternMemory, recentSessions, cycleAwareSuggestions, conditionSuggestions }) {
  const [openGroup, setOpenGroup] = useState(null);

  const suggestedKeys = useMemo(() => {
    const seen = new Set();
    return [...conditionSuggestions, ...cycleAwareSuggestions]
      .filter(k => { if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 6);
  }, [conditionSuggestions, cycleAwareSuggestions]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Suggested chips */}
      {suggestedKeys.length > 0 && (
        <div style={{ background: C.card, borderRadius: 14, padding: '12px 14px', border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace', marginBottom: 8 }}>
            SUGGESTED FOR TODAY
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {suggestedKeys.map(key => {
              const def = SYMPTOM_MAP[key];
              if (!def) return null;
              const active = currentSymptomKeys.has(key);
              return (
                <SymptomChip
                  key={key}
                  def={def}
                  active={active}
                  expanded={expandedSymptom === key}
                  onToggle={() => onToggleSymptom(key)}
                  onExpand={() => onSetExpanded(active ? (expandedSymptom === key ? null : key) : key)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Category group cards */}
      {TAXONOMY.map(group => {
        const isOpen = openGroup === group.key;
        const activeInGroup = group.symptoms.filter(s => currentSymptomKeys.has(s.key)).length;

        return (
          <div key={group.key} style={{ background: C.card, borderRadius: 14, border: `1px solid ${isOpen ? group.color + '44' : C.border}`, overflow: 'hidden' }}>
            {/* Group header — tap to expand */}
            <div
              onClick={() => setOpenGroup(isOpen ? null : group.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}
            >
              <span style={{ fontSize: 18, color: group.color }}>{group.icon}</span>
              <span style={{ flex: 1, fontSize: 13, color: C.text, fontWeight: isOpen ? 700 : 400 }}>{group.label}</span>
              {activeInGroup > 0 && (
                <span style={{ fontSize: 10, background: `${group.color}22`, color: group.color, borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>
                  {activeInGroup}
                </span>
              )}
              <span style={{ color: C.muted, fontSize: 14, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>›</span>
            </div>

            {/* Symptom chips */}
            {isOpen && (
              <div style={{ padding: '0 14px 14px', display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {group.symptoms.map(symptom => {
                  const active = currentSymptomKeys.has(symptom.key);
                  return (
                    <SymptomChip
                      key={symptom.key}
                      def={SYMPTOM_MAP[symptom.key]}
                      active={active}
                      expanded={expandedSymptom === symptom.key}
                      onToggle={() => onToggleSymptom(symptom.key)}
                      onExpand={() => onSetExpanded(active ? (expandedSymptom === symptom.key ? null : symptom.key) : symptom.key)}
                    />
                  );
                })}
              </div>
            )}

            {/* Inline detail for this group's expanded symptom */}
            {isOpen && expandedSymptom && group.symptoms.some(s => s.key === expandedSymptom) && currentSymptomKeys.has(expandedSymptom) && (
              <ExpandedDetail
                symptomKey={expandedSymptom}
                detailMode={detailMode}
                autoSurfaceMap={autoSurfaceMap}
                patternMemory={patternMemory}
                recentSessions={recentSessions}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function SymptomChip({ def, active, expanded, onToggle, onExpand }) {
  return (
    <div style={{ display: 'flex', borderRadius: 20, overflow: 'hidden', border: `1px solid ${active ? def.groupColor : C.border}` }}>
      <button
        onClick={onToggle}
        style={{
          background: active ? `${def.groupColor}22` : C.surface,
          border: 'none',
          color: active ? def.groupColor : C.muted,
          padding: '6px 12px',
          fontSize: 12,
          cursor: 'pointer',
          fontFamily: "'Inter',sans-serif",
          fontWeight: active ? 600 : 400,
        }}
      >
        {def.label}
      </button>
      {active && def.attributes?.length > 0 && (
        <button
          onClick={onExpand}
          style={{
            background: expanded ? `${def.groupColor}33` : `${def.groupColor}15`,
            border: 'none',
            borderLeft: `1px solid ${def.groupColor}44`,
            color: def.groupColor,
            padding: '6px 8px',
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          {expanded ? '▲' : '▼'}
        </button>
      )}
    </div>
  );
}

// Wrapper that reads/writes from session symptom state via context — simpler: pass down via parent
function ExpandedDetail({ symptomKey, detailMode, autoSurfaceMap, patternMemory, recentSessions }) {
  // This is a display wrapper — actual state management is in LoggingFlow
  // We use a custom event to bubble attribute changes up
  return (
    <div style={{ paddingLeft: 14, paddingRight: 14, paddingBottom: 14 }}>
      <LoggingFlowDetailConnector
        symptomKey={symptomKey}
        detailMode={detailMode}
        autoSurfaceMap={autoSurfaceMap}
        patternMemory={patternMemory}
        recentSessions={recentSessions}
      />
    </div>
  );
}

// This needs to be a connected component — we use a ref-forwarding pattern via LoggingFlow context
const LoggingFlowDetailConnector = ({ symptomKey, detailMode, autoSurfaceMap, patternMemory, recentSessions }) => {
  // Reads/writes are handled by the parent LoggingFlow via symptomAttrs state + callback
  // We use a global update function injected via window.__sylvaUpdateAttr (safe in single-tab SPA)
  const [localAttrs, setLocalAttrs] = useState(() => {
    return window.__sylvaGetAttrs?.(symptomKey) || {};
  });

  const handleChange = (attrs) => {
    setLocalAttrs(attrs);
    window.__sylvaSetAttrs?.(symptomKey, attrs);
  };

  return (
    <SymptomDetail
      symptomKey={symptomKey}
      attributes={localAttrs}
      onChange={handleChange}
      detailMode={detailMode}
      autoSurfaceMap={autoSurfaceMap}
      patternMemory={patternMemory}
      recentSessions={recentSessions}
    />
  );
};

// ─── Main LoggingFlow ─────────────────────────────────────────────────────────
export default function LoggingFlow({ user, dayOfCycle, recentSessions = [], onSaved }) {
  const today = localDateStr();

  // Symptom session state
  const [copyForwardSymptoms, setCopyForwardSymptoms] = useState([]);
  const [copyForwardHandled, setCopyForwardHandled] = useState(false);
  const [confirmedFromCopy, setConfirmedFromCopy] = useState([]); // [{key, attributes, source}]
  const [newSymptomKeys, setNewSymptomKeys] = useState(new Set());
  const [symptomAttrs, setSymptomAttrs] = useState({}); // { symptomKey: { attrKey: value } }
  const [expandedSymptom, setExpandedSymptom] = useState(null);
  const [detailMode, setDetailMode] = useState(user?.detail_preference || 'quick');

  const [prevSession, setPrevSession] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [tellYouLaterShown, setTellYouLaterShown] = useState(false);

  // Expose attr accessor/mutator to child connectors
  useEffect(() => {
    window.__sylvaGetAttrs = (key) => symptomAttrs[key] || {};
    window.__sylvaSetAttrs = (key, attrs) => setSymptomAttrs(prev => ({ ...prev, [key]: attrs }));
    return () => { delete window.__sylvaGetAttrs; delete window.__sylvaSetAttrs; };
  }, [symptomAttrs]);

  // Derived data
  const userConditions = useMemo(() => user?.conditions || [], [user]);
  const autoSurfaceMap = useMemo(() => getAutoSurfaceMap(userConditions), [userConditions]);
  const conditionSuggestions = useMemo(() => getConditionSuggestions(userConditions), [userConditions]);
  const cycleAwareSuggestions = useMemo(() => getCycleAwareSuggestions(dayOfCycle), [dayOfCycle]);
  const patternMemory = useMemo(() => computePatternMemory(recentSessions), [recentSessions]);

  // All symptom keys currently in the session (from copy-forward + newly added)
  const allSymptomKeys = useMemo(() => {
    const s = new Set(newSymptomKeys);
    confirmedFromCopy.forEach(c => { if (c.source !== 'gone') s.add(c.key); });
    return s;
  }, [newSymptomKeys, confirmedFromCopy]);

  // Fetch previous session on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    axios.get(`${API_URL}/api/sessions/latest`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data) {
          setPrevSession(r.data);
          const carry = getCopyForwardSymptoms(r.data);
          setCopyForwardSymptoms(carry);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingPrev(false));
  }, []);

  // Copy-forward action handler
  const handleCopyForwardAction = useCallback((confirmed) => {
    setConfirmedFromCopy(confirmed);
    // Pre-fill attrs from confirmed copy-forward
    const attrUpdates = {};
    confirmed.forEach(s => { attrUpdates[s.key] = { ...s.attributes }; });
    setSymptomAttrs(prev => ({ ...attrUpdates, ...prev }));
    setCopyForwardHandled(true);
  }, []);

  // Toggle a new symptom on/off
  const toggleSymptom = useCallback((key) => {
    setNewSymptomKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (expandedSymptom === key) setExpandedSymptom(null);
      } else {
        next.add(key);
        // Pre-fill with pattern memory if available
        const mem = patternMemory[key];
        if (mem) {
          setSymptomAttrs(p => ({ ...p, [key]: { ...mem.commonAttributes } }));
        }
      }
      return next;
    });
  }, [expandedSymptom, patternMemory]);

  // Build the final session payload
  const buildSession = useCallback((draft = false) => {
    const symptoms = [];

    confirmedFromCopy.forEach(s => {
      symptoms.push({ key: s.key, attributes: symptomAttrs[s.key] || s.attributes, source: 'copy_forward' });
    });
    newSymptomKeys.forEach(key => {
      symptoms.push({ key, attributes: symptomAttrs[key] || {}, source: 'user' });
    });

    return { session_date: today, symptoms, draft };
  }, [confirmedFromCopy, newSymptomKeys, symptomAttrs, today]);

  // Save
  const handleSave = async () => {
    if (allSymptomKeys.size === 0 && confirmedFromCopy.length === 0) return;
    setSaving(true);
    const token = localStorage.getItem('authToken');
    try {
      const payload = buildSession(false);
      if (draftId) {
        await axios.patch(`${API_URL}/api/sessions/${draftId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/api/sessions`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSaved(true);
      setDraftId(null);
      onSaved?.();
    } catch (err) {}
    setSaving(false);
  };

  // Tell you later — saves a draft
  const handleTellYouLater = async () => {
    setTellYouLaterShown(true);
    const token = localStorage.getItem('authToken');
    try {
      const payload = buildSession(true);
      const r = await axios.post(`${API_URL}/api/sessions`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setDraftId(r.data?.id);
    } catch (err) {}
  };

  const totalLogged = allSymptomKeys.size + (copyForwardHandled ? confirmedFromCopy.length : 0);

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loadingPrev) {
    return (
      <div style={{ background: C.card, borderRadius: 18, padding: '20px 16px', border: `1px solid ${C.border}`, textAlign: 'center' }}>
        <div style={{ color: C.muted, fontSize: 12, fontFamily: 'monospace' }}>LOADING YOUR LOG…</div>
      </div>
    );
  }

  if (saved) {
    return (
      <div style={{ background: C.card, borderRadius: 18, padding: '20px 16px', border: `1px solid ${C.green}44`, textAlign: 'center' }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
        <div style={{ color: C.green, fontSize: 14, fontWeight: 600 }}>Logged</div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
          {totalLogged} symptom{totalLogged !== 1 ? 's' : ''} saved
        </div>
      </div>
    );
  }

  if (tellYouLaterShown) {
    return (
      <div style={{ background: C.card, borderRadius: 18, padding: '20px 16px', border: `1px solid ${C.border}`, textAlign: 'center' }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No problem — come back when you're ready.</div>
        <div style={{ color: C.muted, fontSize: 12 }}>Your draft is saved. We'll remind you later.</div>
        <button
          onClick={() => setTellYouLaterShown(false)}
          style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, cursor: 'pointer', marginTop: 12 }}
        >
          Actually, log now →
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Detail mode toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Log symptoms</div>
        <div style={{ display: 'flex', gap: 2, background: C.border, padding: 2, borderRadius: 8 }}>
          {['quick', 'full'].map(mode => (
            <button
              key={mode}
              onClick={() => setDetailMode(mode)}
              style={{
                background: detailMode === mode ? C.surface : 'transparent',
                border: 'none',
                color: detailMode === mode ? C.text : C.muted,
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: detailMode === mode ? 700 : 400,
                cursor: 'pointer',
                fontFamily: "'Inter',sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: detailMode === mode ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
              }}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Copy-forward panel */}
      {!copyForwardHandled && copyForwardSymptoms.length > 0 && (
        <CopyForwardPanel
          carrySymptoms={copyForwardSymptoms}
          prevSession={prevSession}
          onAction={handleCopyForwardAction}
          onTellYouLater={handleTellYouLater}
          detailMode={detailMode}
          autoSurfaceMap={autoSurfaceMap}
          patternMemory={patternMemory}
          recentSessions={recentSessions}
        />
      )}

      {/* Category picker — always shown */}
      <div style={{ marginBottom: 14 }}>
        <CategoryPicker
          currentSymptomKeys={allSymptomKeys}
          onToggleSymptom={toggleSymptom}
          expandedSymptom={expandedSymptom}
          onSetExpanded={setExpandedSymptom}
          detailMode={detailMode}
          autoSurfaceMap={autoSurfaceMap}
          patternMemory={patternMemory}
          recentSessions={recentSessions}
          cycleAwareSuggestions={cycleAwareSuggestions}
          conditionSuggestions={conditionSuggestions}
        />
      </div>

      {/* Save / Tell you later */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          onClick={handleSave}
          disabled={saving || (allSymptomKeys.size === 0 && confirmedFromCopy.length === 0)}
          style={{
            flex: 1,
            background: (allSymptomKeys.size > 0 || confirmedFromCopy.length > 0) ? C.accent : C.border,
            color: (allSymptomKeys.size > 0 || confirmedFromCopy.length > 0) ? '#0D0D12' : C.muted,
            border: 'none',
            borderRadius: 12,
            padding: '13px',
            fontSize: 14,
            fontWeight: 700,
            cursor: saving ? 'wait' : 'pointer',
            fontFamily: "'Inter',sans-serif",
          }}
        >
          {saving ? 'Saving…' : `Save log${totalLogged > 0 ? ` (${totalLogged})` : ''}`}
        </button>
        {!copyForwardHandled && copyForwardSymptoms.length === 0 && (
          <button
            onClick={handleTellYouLater}
            style={{ background: 'none', border: `1px solid ${C.border}`, color: C.muted, borderRadius: 12, padding: '13px 14px', fontSize: 12, cursor: 'pointer', fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap' }}
          >
            Later
          </button>
        )}
      </div>
    </div>
  );
}
